import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_API_PROVIDED_BY, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { RiUploadCloud2Line } from '@remixicon/react';
import { TypeBadge } from '../badges/type-badge.component';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './relations.module.css';

const ProvidingComponentsContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '50px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('providingComponents.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('providingComponents.empty')} />;
  }
  return (
    <Flex direction="column" gap="3">
      {entities.map(component => {
        const ref = `${component.kind}:${component.metadata.namespace || 'default'}/${component.metadata.name}`;
        const type = (component.spec?.type as string) || undefined;
        return (
          <Tile
            key={ref}
            href={`/catalog/${component.metadata.namespace || 'default'}/${component.kind.toLowerCase()}/${component.metadata.name}`}
            title={component.metadata.title || component.metadata.name}
            trailing={type ? <TypeBadge type={type} /> : undefined}
          />
        );
      })}
    </Flex>
  );
};

export const ProvidingComponentsCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_API_PROVIDED_BY,
    kind: 'component',
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiUploadCloud2Line size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('providingComponents.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <ProvidingComponentsContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
